import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { dashboardPathFor } from "@/lib/uiConstants";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { user, register, refresh } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const inviteToken = search.get("invite");

  const [form, setForm] = useState({ email: "", password: "", name: "", role: "sonographer", clinic_name: "", invite_token: inviteToken || "", sonography_canada_number: "" });
  const [invitePreview, setInvitePreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${inviteToken}`);
        setInvitePreview(data);
        setForm((f) => ({ ...f, email: data.email, role: "sonographer", invite_token: inviteToken }));
      } catch {
        setErr("Invitation link is invalid or expired.");
      }
    })();
  }, [inviteToken]);

  const acceptAsLoggedIn = async () => {
    setErr("");
    setAccepting(true);
    try {
      await api.post("/invitations/accept", { token: inviteToken });
      await refresh();
      nav("/app/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAccepting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register(form);
      nav(dashboardPathFor(u));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  let submitLabel = "Create account";
  if (busy) submitLabel = "Creating\u2026";
  else if (invitePreview) submitLabel = "Join clinic";

  // Logged-in branch: existing user accepting an invite
  if (user && invitePreview) {
    const emailMatches = user.email?.toLowerCase() === invitePreview.email?.toLowerCase();
    return (
      <div className="container-x py-14 max-w-md">
        <h1 className="text-3xl font-bold">Join {invitePreview.clinic_name}</h1>
        <p className="text-zinc-600 text-sm mt-1">You&apos;re signed in as <span className="font-semibold">{user.email}</span>.</p>
        {!emailMatches ? (
          <div className="card-elev mt-6 flex items-start gap-3" data-testid="invite-email-mismatch">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600" />
            <div>
              <div className="font-semibold">This invitation is for a different email</div>
              <div className="text-sm text-zinc-600 mt-1">It was sent to <strong>{invitePreview.email}</strong>. Sign out and create a new account using that email, or have your clinic manager re-send the invite to <strong>{user.email}</strong>.</div>
            </div>
          </div>
        ) : (
          <div className="card-elev mt-6">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" style={{ color: "var(--gold-hover)" }} /><span className="font-semibold">Ready to join</span></div>
            <p className="text-sm text-zinc-600 mt-2">Accepting will link your account to <strong>{invitePreview.clinic_name}</strong>. Your personal pain data remains private to you — your clinic manager only sees anonymized aggregates.</p>
            {err && <div className="text-sm text-red-600 mt-3" data-testid="accept-error">{err}</div>}
            <button onClick={acceptAsLoggedIn} disabled={accepting} className="btn-gold w-full mt-4" data-testid="accept-invite">
              {accepting ? "Joining…" : "Accept and join clinic"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container-x py-14 max-w-md">
      <h1 className="text-3xl font-bold">{invitePreview ? "Join your clinic" : "Create your account"}</h1>
      <p className="text-zinc-600 text-sm mt-1">
        {invitePreview ? <>You&apos;ve been invited to join <span className="font-semibold">{invitePreview.clinic_name}</span>.</>
          : "Start your 14-day free trial. No credit card required."}
      </p>
      {invitePreview && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-lg" style={{ background: "var(--gold-light)" }} data-testid="invite-preview">
          <CheckCircle2 className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          <div className="text-sm text-zinc-700">Invitation accepted — your account will be linked to <strong>{invitePreview.clinic_name}</strong>.</div>
        </div>
      )}
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
        {!invitePreview && (
          <>
            <button type="button" onClick={googleLogin} className="btn-outline w-full" data-testid="register-google-btn">
              Continue with Google
            </button>
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              <span className="flex-1 border-t border-zinc-200" />OR<span className="flex-1 border-t border-zinc-200" />
            </div>
          </>
        )}
        <div>
          <label className="text-sm font-semibold">Full name</label>
          <input required className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
        </div>
        <div>
          <label className="text-sm font-semibold">Username</label>
          <input
            required
            minLength={3}
            maxLength={30}
            pattern="[A-Za-z0-9._\-]{3,30}"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="your_username"
            className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            data-testid="register-username"
          />
          <p className="text-[11px] text-zinc-400 mt-1">3–30 characters · letters, numbers, '.', '_' or '-' · you'll use this to sign in.</p>
        </div>
        <div>
          <label className="text-sm font-semibold">Email</label>
          <input type="email" required disabled={!!invitePreview} className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none disabled:bg-zinc-50" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
        </div>
        <div>
          <label className="text-sm font-semibold">Password</label>
          <input type="password" required minLength={6} className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
        </div>
        {!invitePreview && (
          <div>
            <label className="text-sm font-semibold">I am a</label>
            <select className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="register-role">
              <option value="sonographer">Sonographer (independent)</option>
              <option value="clinic_manager">Clinic Manager — I want to register my clinic</option>
            </select>
          </div>
        )}
        {form.role === "clinic_manager" && !invitePreview && (
          <div>
            <label className="text-sm font-semibold">Clinic name</label>
            <input required className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} data-testid="register-clinic-name" />
          </div>
        )}
        {form.role === "sonographer" && (
          <div>
            <label className="text-sm font-semibold">Sonography Canada number <span className="text-xs font-normal text-zinc-400">(optional)</span></label>
            <input
              type="text"
              maxLength={40}
              placeholder="e.g. SCA-12345"
              value={form.sonography_canada_number}
              onChange={(e) => setForm({ ...form, sonography_canada_number: e.target.value })}
              className="mt-2 w-full h-12 rounded-md border border-zinc-200 px-4 focus:ring-2 focus:ring-[#d4af37] outline-none font-mono tracking-wide"
              data-testid="register-sca"
            />
            <p className="text-[11px] text-zinc-400 mt-1">Your clinic manager can also add or update this later.</p>
          </div>
        )}
        {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}
        <button disabled={busy} className="btn-gold w-full" data-testid="register-submit">{submitLabel}</button>
        <div className="text-sm text-zinc-600">
          Already have an account? <Link to="/login" className="font-semibold" style={{ color: "var(--gold-hover)" }}>Sign in</Link>
        </div>
      </form>
    </div>
  );
}
